import styled from 'styled-components';
import { LayoutColors } from '@src/core/theme';
import { BodyExtraSmall, BodyRegularSemiBold, Heading4, Heading6 } from '@src/fonts/typography/Typography';
import useTranslate from '../../i18n/useTranslate';
import CardIcon from '../../icons/secure_payment.svg';
import { useAppDispatch, useAppSelector, useWidgetParam } from '@src/core/hooks';
import { setStepCheckout } from '@src/features/checkout';
import { ClassNamespace, extractValues, getParsedCardValidityValues } from '@src/common';
import {
  Order,
  Sale,
  PaymentOption,
  PaymentFingerprint,
  DirectLink,
  PaymentMethodCode,
} from '@src/core/buyerApiClient';
import { setupFingerprint } from '@src/common/fingerprint';
import React, { FormEvent, useEffect, useState, useMemo } from 'react';
import { CardPaymentForm } from './CardPaymentForm';
import { PixPaymentForm } from './PixPaymentForm';
import { device } from '@src/common/breakpoints';
import { PurchaseResume } from './PurchaseResume';
import { PaymentMethodFormProps } from './types';
import { useWidgetId } from '@src/core/IdProvider';
import {
  Transactable,
  paymentErrorsSelector,
  paymentOngoingSelector,
  processPayment,
  getPaymentStatus,
  loadPaymentOptions,
  paymentOptionSelector,
} from '@src/features/payment';
import { track } from '@src/services/analytics';
import PhoneVerificationAlert from '../PhoneVerificationAlert';
import { buyerSelector } from '@src/features/auth';
import { ProviderFinancialError } from './ProviderFinancialError';
import Loading from '@src/components/ui2/Loading';

interface PaymentFormProps {
  cn?: ClassNamespace;
  order?: Order;
  sale?: Sale;
  directLink?: DirectLink;
}

const validPaymentMethodCodes: PaymentMethodCode[] = [
  PaymentMethodCode.CreditCard,
  PaymentMethodCode.DebitCard,
  PaymentMethodCode.Pix,
];

const sortPaymentOptions = (opt: PaymentOption) => (opt.paymentMethod.code === PaymentMethodCode.Pix ? -1 : 1);

const transactableIsExpired = (tr: Transactable) => {
  if (tr.order) {
    return !tr.order.isPayable;
  }

  if (tr.sale) {
    return !tr.sale.isPayable;
  }

  if (tr.directLink) {
    return tr.directLink.isExpired;
  }

  return false;
};

const PaymentForm = (props: PaymentFormProps) => {
  const { cn, order, sale, directLink } = props;

  const dispatch = useAppDispatch();
  const { i18nElement, i18n } = useTranslate();
  const widgetId = useWidgetId();
  const inputClass = useWidgetParam('input-class') || 'input';
  const [buyer] = useAppSelector(buyerSelector);

  const paymentOngoing = useAppSelector(paymentOngoingSelector);
  const errors = useAppSelector(paymentErrorsSelector);

  const [selectedPaymentOption, setSelectedPaymentOption] = useState<PaymentOption | undefined>(undefined);

  const transactable = {
    order,
    sale,
    directLink,
  };

  const transactablePaymentOptions = useAppSelector(paymentOptionSelector(transactable));
  const validMethods = useMemo(() => {
    return (
      transactablePaymentOptions?.list
        .filter((opt) => validPaymentMethodCodes.includes(opt.paymentMethod.code))
        ?.sort(sortPaymentOptions) ?? []
    );
  }, [transactablePaymentOptions?.list]);

  const handlePaymentSubmit = (ev: FormEvent<HTMLFormElement>) => {
    ev.preventDefault();

    const { paymentMethodId, cardValidity, ...remaining } = extractValues(ev.target as HTMLFormElement);

    const { validityMonth = '', validityYear = '' } = cardValidity ? getParsedCardValidityValues(cardValidity) : {};

    track('checkoutPaymentAttempt');

    dispatch(
      processPayment({
        order,
        sale,
        directLink,
        paymentMethodId: `${paymentMethodId}`,
        paymentCard:
          Object.keys(remaining).length > 0
            ? {
                validityYear,
                validityMonth,
                cardNumber: remaining.cardNumber.replace(/\s/g, ''),
                securityCode: remaining.securityCode,
                holder: remaining.holder,
                holderIdentity: remaining.holderIdentity?.replace(/\W/g, '') ?? null,
                installments: parseInt(remaining.installments, 10),
                postalCode: remaining.postalCode?.replace(/\W/g, ''),
              }
            : undefined,
      }),
    );
  };

  const isSelected = (opt: PaymentOption) =>
    selectedPaymentOption != undefined && opt.paymentMethod.id === selectedPaymentOption.paymentMethod.id;

  const needsPhoneVerification =
    directLink?.type !== 'payment' && selectedPaymentOption?.needsPhoneVerification && !buyer?.phoneVerifiedAt;

  useEffect(() => {
    track('checkoutPaymentFormAccess');

    dispatch(setStepCheckout({ id: widgetId || '', value: 'payment' }));
  }, []);

  useEffect(() => {
    if (!transactablePaymentOptions) {
      dispatch(loadPaymentOptions(transactable));
    }
  }, [transactable, transactablePaymentOptions]);

  useEffect(() => {
    if (transactablePaymentOptions && !transactablePaymentOptions.loading) {
      const fingerprints: { [key: string]: PaymentFingerprint } = {};
      for (const opt of validMethods) {
        if (opt.fingerprints) {
          for (const fingerprint of opt.fingerprints) {
            fingerprints[fingerprint.type] = fingerprint;
          }
        }
      }

      for (const key in fingerprints) {
        const fp = fingerprints[key]!;
        setupFingerprint(fp);
      }
    }
  }, [transactablePaymentOptions?.loading]);

  useEffect(() => {
    if (validMethods.length > 0 && !selectedPaymentOption) {
      setSelectedPaymentOption(validMethods[0]!);
    }
  }, [validMethods, selectedPaymentOption]);

  return (
    <>
      {order || sale ? (
        <PurchaseResumeResponsive>
          <PurchaseResume isResponsive={true} paymentOption={selectedPaymentOption} />
        </PurchaseResumeResponsive>
      ) : null}

      <PaymentContainer data-cy='checkout_paymentForm'>
        <ContainerHeader>
          <HeaderTitle>{i18nElement('checkout_step_payment')}</HeaderTitle>
          <HeaderSecurityNote>
            <SecurePaymentIcon>
              <CardIcon />
            </SecurePaymentIcon>
            <SecurePaymentNote>{i18nElement('checkout_step_payment_security_note')}</SecurePaymentNote>
          </HeaderSecurityNote>
        </ContainerHeader>

        <ContainerBody>
          <PaymentForms showResume={!!sale}>
            {transactableIsExpired(transactable) ? (
              <ExpiredSales>
                <p>
                  <strong>{i18nElement('checkout_expiredSale')}</strong>
                </p>
                <p>{i18nElement('checkout_makeYourPurchaseAgain')}</p>
              </ExpiredSales>
            ) : validMethods.length > 0 ? (
              <PaymentFormsContainer>
                {paymentOngoing ? <LoadingCardPayment /> : null}

                <PaymentMethods paymentOngoing={paymentOngoing && !directLink}>
                  <MethodHead>
                    {validMethods.map((opt) => (
                      <Method
                        key={opt.paymentMethod.id}
                        isSelected={isSelected(opt)}
                        onClick={() => setSelectedPaymentOption(opt)}
                      >
                        <span>{opt.paymentMethod.name}</span>
                        {opt.paymentMethod.discountPercentage ? (
                          <span>{` (${opt.paymentMethod.discountPercentage.toString().replace('.', ',')}${i18n(
                            'checkout_percentDiscount',
                          )})`}</span>
                        ) : null}
                      </Method>
                    ))}
                  </MethodHead>

                  {needsPhoneVerification ? (
                    <PhoneVerificationBody>
                      <PhoneVerificationAlert />
                    </PhoneVerificationBody>
                  ) : (
                    <MethodBody>
                      {renderPaymentForm({
                        cn,
                        order,
                        sale,
                        directLink,
                        paymentOption: selectedPaymentOption,
                        handleSubmit: handlePaymentSubmit,
                        saleStatusRequester: () => dispatch(getPaymentStatus({ directLink, sale, order })),
                        errors,
                        inputClass,
                        unavailableMessage: i18n('checkout_unavailablePaymentMethod'),
                      })}
                    </MethodBody>
                  )}
                </PaymentMethods>
              </PaymentFormsContainer>
            ) : transactablePaymentOptions?.loading ? (
              <Loading size={24} fullWidth={false} />
            ) : (
              <ProviderFinancialError />
            )}
          </PaymentForms>

          {order || sale ? (
            <PurchaseResumeDesktop>
              <PurchaseResume paymentOption={selectedPaymentOption} />
            </PurchaseResumeDesktop>
          ) : null}
        </ContainerBody>
      </PaymentContainer>
    </>
  );
};

const renderPaymentForm = (props: PaymentMethodFormProps) => {
  switch (props.paymentOption?.paymentMethod.code) {
    case PaymentMethodCode.CreditCard:
    case PaymentMethodCode.DebitCard:
      return <CardPaymentForm {...props} />;
    case PaymentMethodCode.Pix:
      return <PixPaymentForm {...props} />;
    default:
      return <div>{props.unavailableMessage}</div>;
  }
};

interface PaymentMethodProps {
  isSelected: boolean;
}

interface PaymentMethodsProps {
  paymentOngoing: boolean;
}

interface PaymentFormsProps {
  showResume: boolean;
}

const PaymentContainer = styled.div`
  width: 100%;
  max-width: 1095px;
  height: auto;
  border-radius: 8px;
  padding: 16px;
  background-color: ${LayoutColors.whiteDark};
  margin: auto;
  display: flex;
  flex-direction: column;
  gap: 40px;

  ${device.mediumTablet} {
    padding: 24px;
  }
`;

const ContainerBody = styled.div`
  display: flex;
  width: 100%;
  gap: 0 16px;
`;

const PaymentForms = styled.div<PaymentFormsProps>`
  ${(props) => (props.showResume ? 'max-width: 733px;' : '')}

  width: 100%;
  position: relative;
  ${device.mediumTablet} {
    ${(props) => (props.showResume ? 'width: calc(100% - 298px);' : '')}
  }
`;

const ExpiredSales = styled.div``;

const PaymentFormsContainer = styled.div`
  width: 100%;
  height: auto;
  position: relative;
`;

const PaymentMethods = styled.div<PaymentMethodsProps>`
  display: flex;
  flex-flow: column;
  ${(props) => (props.paymentOngoing ? 'opacity: 0.5; pointer-events: none;' : '')}
`;

const MethodHead = styled.div`
  display: flex;
  flex-flow: column;
  gap: 8px;

  ${device.smallTablet} {
    flex-flow: row;
  }
`;

const Method = styled.button<PaymentMethodProps>`
  ${BodyRegularSemiBold};
  width: 100%;
  height: 44px;
  border-radius: 4px;
  text-align: center;
  background-color: ${(props) => (props.isSelected ? LayoutColors.justBlue : 'transparent')};
  border: 1px solid ${LayoutColors.justBlue};
  color: ${(props) => (props.isSelected ? LayoutColors.whiteLight : LayoutColors.justBlue)};
  cursor: pointer;
`;

const MethodBody = styled.div`
  margin-top: 16px;
  border: 1px solid #cdcdcd;
  border-radius: 4px;
  padding: 16px;
`;

const PhoneVerificationBody = styled.div`
  margin-top: 16px;
`;

const ContainerHeader = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;

  ${device.smallTablet} {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
  }
`;

const HeaderTitle = styled.h1`
  ${Heading6}
  color: ${LayoutColors.blackDark};

  ${device.mediumTablet} {
    ${Heading4};
    color: ${LayoutColors.blackDark};
  }
`;

const HeaderSecurityNote = styled.div`
  display: flex;
  align-items: center;
  gap: 0 8px;

  ${device.smallTablet} {
    max-width: 390px;
  }
`;

const SecurePaymentIcon = styled.div`
  width: 36px;
`;

const SecurePaymentNote = styled.p`
  ${BodyExtraSmall};
`;

const LoadingCardPayment = styled.div`
  width: 82px;
  height: 82px;
  border-radius: 100%;
  border: 5px solid ${LayoutColors.justBlue};
  border-bottom-color: rgba(0, 0, 0, 0);
  animation: Rotate 0.5s linear infinite;
  z-index: 999999999;
  position: absolute;
  left: calc(50% - 41px);
  top: calc(50% - 41px);
  @keyframes Rotate {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const PurchaseResumeDesktop = styled.div`
  width: 298px;
  display: none;
  ${device.mediumTablet} {
    display: initial;
    flex-shrink: 0;
  }
`;

const PurchaseResumeResponsive = styled.div`
  width: 100%;
  display: block;
  ${device.mediumTablet} {
    display: none;
  }
  margin-bottom: 16px;
`;

export default PaymentForm;
